import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Edit2, Trash2, Save, X, Loader2,
  GripVertical, Eye, EyeOff, Shield, ChevronRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../contexts/NotificationContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Service Item Component
const SortableServiceItem = ({ service, language, t, onEdit, onDelete, onToggleActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-900 border rounded-xl p-4 flex items-center gap-4 ${service.is_active ? 'border-gray-200 dark:border-gray-800' : 'border-red-200 dark:border-red-900 opacity-60'
        } ${isDragging ? 'shadow-2xl ring-2 ring-blue-500' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300 p-1"
        data-testid={`drag-handle-${service.slug}`}
      >
        <GripVertical className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-gray-900 dark:text-white">{service.title_tr}</span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600 dark:text-gray-400">{service.title_en}</span>
        </div>
        <p className="text-sm text-gray-500 truncate">/{service.slug}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggleActive(service.id, service.is_active)}
          className={`p-2 rounded-lg ${service.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
          title={service.is_active ? 'Active' : 'Inactive'}
          data-testid={`toggle-active-${service.slug}`}
        >
          {service.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
        </button>
        <button
          onClick={() => onEdit(service)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
          title={t.edit}
          data-testid={`edit-service-${service.slug}`}
        >
          <Edit2 className="w-5 h-5" />
        </button>
        <button
          onClick={() => onDelete(service.id)}
          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
          title={t.delete}
          data-testid={`delete-service-${service.slug}`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const AdminServices = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { showModal } = useNotification();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    description_tr: '',
    description_en: '',
    content_tr: '',
    content_en: '',
    features_tr: '',
    features_en: '',
    icon: 'code',
    is_active: true
  });

  const iconOptions = [
    { value: 'code', label: 'Code' },
    { value: 'shopping-cart', label: 'Shopping Cart' },
    { value: 'zap', label: 'Zap' },
    { value: 'bot', label: 'Bot' },
    { value: 'box', label: 'Box' },
    { value: 'trending-up', label: 'Trending Up' }
  ];

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadServices();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('sort_order', { ascending: true });

      if (!error) setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = services.findIndex((s) => s.id === active.id);
    const newIndex = services.findIndex((s) => s.id === over.id);

    const newServices = arrayMove(services, oldIndex, newIndex);
    setServices(newServices);

    // Save new order to database
    setReordering(true);
    try {
      const updates = newServices.map((service, index) => ({
        id: service.id,
        sort_order: index + 1
      }));

      // Update each service's sort_order
      for (const update of updates) {
        await supabase
          .from('services')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error saving order:', error);
      // Reload on error to restore correct order
      await loadServices();
    } finally {
      setReordering(false);
    }
  };

  const handleEdit = (service) => {
    setEditingId(service.id);
    setIsCreating(false);
    setFormData({
      slug: service.slug || '',
      title_tr: service.title_tr || '',
      title_en: service.title_en || '',
      description_tr: service.description_tr || '',
      description_en: service.description_en || '',
      content_tr: service.content_tr || '',
      content_en: service.content_en || '',
      features_tr: Array.isArray(service.features_tr) ? JSON.stringify(service.features_tr, null, 2) : (service.features_tr || '[]'),
      features_en: Array.isArray(service.features_en) ? JSON.stringify(service.features_en, null, 2) : (service.features_en || '[]'),
      icon: service.icon || 'code',
      is_active: service.is_active !== false
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      slug: '',
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      content_tr: '',
      content_en: '',
      features_tr: '[]',
      features_en: '[]',
      icon: 'code',
      is_active: true
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Parse features JSON
      let features_tr, features_en;
      try {
        features_tr = JSON.parse(formData.features_tr);
        features_en = JSON.parse(formData.features_en);
      } catch (e) {
        showModal(
          language === 'tr' ? 'Hata' : 'Error',
          language === 'tr' ? 'Özellikler alanında geçersiz JSON formatı' : 'Invalid JSON in features field',
          'error'
        );
        setSaving(false);
        return;
      }

      const saveData = {
        slug: formData.slug,
        title_tr: formData.title_tr,
        title_en: formData.title_en,
        description_tr: formData.description_tr,
        description_en: formData.description_en,
        content_tr: formData.content_tr,
        content_en: formData.content_en,
        features_tr,
        features_en,
        icon: formData.icon,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      if (isCreating) {
        saveData.sort_order = services.length + 1;
        await supabase.from('services').insert(saveData);
      } else {
        await supabase.from('services').update(saveData).eq('id', editingId);
      }

      await loadServices();
      handleCancel();
    } catch (error) {
      console.error('Error saving:', error);
      // If it's a body stream error, the save likely succeeded - reload to check
      if (error.message?.includes('body stream')) {
        await loadServices();
        handleCancel();
      } else {
        showModal(
          language === 'tr' ? 'Hata' : 'Error',
          (language === 'tr' ? 'Kaydedilirken hata oluştu: ' : 'Error saving: ') + error.message,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(language === 'tr' ? 'Bu hizmeti silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await supabase.from('services').delete().eq('id', id);
      await loadServices();
    } catch (error) {
      console.error('Error deleting:', error);
      if (!error.message?.includes('body stream')) {
        showModal(
          language === 'tr' ? 'Hata' : 'Error',
          (language === 'tr' ? 'Silinirken hata oluştu: ' : 'Error deleting: ') + error.message,
          'error'
        );
      }
      await loadServices();
    }
  };

  const toggleActive = async (id, currentState) => {
    try {
      await supabase
        .from('services')
        .update({ is_active: !currentState })
        .eq('id', id);
      await loadServices();
    } catch (error) {
      console.error('Error toggling:', error);
      await loadServices();
    }
  };

  const texts = {
    tr: {
      title: 'Hizmet Yönetimi',
      addNew: 'Yeni Hizmet',
      edit: 'Düzenle',
      delete: 'Sil',
      save: 'Kaydet',
      cancel: 'İptal',
      slug: 'URL Slug',
      titleTr: 'Başlık (TR)',
      titleEn: 'Başlık (EN)',
      descTr: 'Açıklama (TR)',
      descEn: 'Açıklama (EN)',
      contentTr: 'İçerik (TR)',
      contentEn: 'İçerik (EN)',
      featuresTr: 'Özellikler (TR) - JSON Array',
      featuresEn: 'Özellikler (EN) - JSON Array',
      icon: 'İkon',
      active: 'Aktif',
      noServices: 'Henüz hizmet yok',
      dragHint: 'Sıralamayı değiştirmek için sürükleyin',
      reordering: 'Kaydediliyor...'
    },
    en: {
      title: 'Service Management',
      addNew: 'New Service',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      slug: 'URL Slug',
      titleTr: 'Title (TR)',
      titleEn: 'Title (EN)',
      descTr: 'Description (TR)',
      descEn: 'Description (EN)',
      contentTr: 'Content (TR)',
      contentEn: 'Content (EN)',
      featuresTr: 'Features (TR) - JSON Array',
      featuresEn: 'Features (EN) - JSON Array',
      icon: 'Icon',
      active: 'Active',
      noServices: 'No services yet',
      dragHint: 'Drag to reorder',
      reordering: 'Saving...'
    }
  };

  const t = texts[language] || texts['tr'];

  // Loading state
  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  // Access denied
  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-xl">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="admin-services-page">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/admin" className="hover:text-blue-600 dark:hover:text-blue-400">
              Admin
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">{t.title}</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
              {reordering && (
                <span className="text-sm text-blue-600 animate-pulse">{t.reordering}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreate}
                disabled={isCreating || editingId}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="add-service-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.addNew}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create/Edit Form */}
        {(isCreating || editingId) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8"
            data-testid="service-form"
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
              {isCreating ? t.addNew : t.edit}
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.slug}</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="web-app"
                  data-testid="service-slug-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.icon}</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="service-icon-select"
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleTr}</label>
                <input
                  type="text"
                  value={formData.title_tr}
                  onChange={(e) => setFormData({ ...formData, title_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="service-title-tr-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleEn}</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="service-title-en-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.descTr}</label>
                <textarea
                  rows={2}
                  value={formData.description_tr}
                  onChange={(e) => setFormData({ ...formData, description_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                  data-testid="service-desc-tr-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.descEn}</label>
                <textarea
                  rows={2}
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                  data-testid="service-desc-en-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.contentTr}</label>
                <textarea
                  rows={3}
                  value={formData.content_tr}
                  onChange={(e) => setFormData({ ...formData, content_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                  data-testid="service-content-tr-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.contentEn}</label>
                <textarea
                  rows={3}
                  value={formData.content_en}
                  onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                  data-testid="service-content-en-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.featuresTr}</label>
                <textarea
                  rows={4}
                  value={formData.features_tr}
                  onChange={(e) => setFormData({ ...formData, features_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none text-gray-900 dark:text-white"
                  placeholder='["Feature 1", "Feature 2"]'
                  data-testid="service-features-tr-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.featuresEn}</label>
                <textarea
                  rows={4}
                  value={formData.features_en}
                  onChange={(e) => setFormData({ ...formData, features_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none text-gray-900 dark:text-white"
                  placeholder='["Feature 1", "Feature 2"]'
                  data-testid="service-features-en-input"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                    data-testid="service-active-checkbox"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t.active}</span>
                </label>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="save-service-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t.save}
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                data-testid="cancel-service-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {t.cancel}
              </button>
            </div>
          </motion.div>
        )}

        {/* Services List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
            <p className="text-gray-500">{t.noServices}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex items-center gap-2">
              <GripVertical className="w-4 h-4" />
              {t.dragHint}
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={services.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4" data-testid="services-list">
                  {services.map((service) => (
                    <SortableServiceItem
                      key={service.id}
                      service={service}
                      language={language}
                      t={t}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleActive={toggleActive}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminServices;
